import { FC, memo, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { cn, Select } from '@crisellevaldez/speculo-ui';
import { Phone, Mail, MessageSquare, Clock, Search } from 'lucide-react';
import { getActivityIcon, getActivityIconBg } from '../../utils/activity/activityHelpers';
import { formatPhoneNumber } from '../../utils/formatters';
import { ActivityDescriptionBlock, ActivityInlineBadges } from '../common/ActivityContent';
import { CallDetailsModal, type CallRecord } from '../features/calls/CallDetailsModal';
import { fetchCallRecordById } from '../features/calls/fetchCallRecord';
import { SmsThreadModal } from '../features/dashboard/SmsThreadModal';
import { RecentActivities } from './RecentActivities';
import { LiveIndicator } from '../common/LiveIndicator';
import { useHotLeadsGQL, type GQLHotLead } from '../../hooks/graphql/dashboard/useHotLeadsGQL';
import { useFlywheelEnabled, useOrganizationStore } from '../../stores/useOrganizationStore';
import { useDebounce } from '../../hooks/useDebounce';
import { getRelativeDateString } from '../../utils/relativeDateFormat';

/* ─────────────────────────────────────────────────────────────────
 * Lead Activity dashboard table — one row per lead, hottest first.
 * Backed by organization.hotLeads (SPEC-1707).
 *
 *   1. Lead          — hollow score circle + name + phone
 *   2. Next touch    — Remi's next scheduled cadence step
 *   3. Remi activity — per-channel bars (calls / SMS / email)
 *   4. Last from lead — last inbound event (channel + relative time + preview)
 * ───────────────────────────────────────────────────────────────── */

type ChannelKey = 'sms' | 'call' | 'email';

const SORT_OPTIONS = [
  { value: 'score', label: 'Hottest first' },
  { value: 'lastFrom', label: 'Most recent' },
];

const NEXT_TOUCH_CHANNEL_META: Record<ChannelKey, { icon: React.ReactNode; color: string }> = {
  sms: { icon: <MessageSquare className="w-3 h-3" />, color: '#7c3aed' },
  call: { icon: <Phone className="w-3 h-3" />, color: '#059669' },
  email: { icon: <Mail className="w-3 h-3" />, color: '#2563eb' },
};

/** Normalize a backend channel string to a known key (default 'sms'). */
function toChannelKey(raw: string | null | undefined): ChannelKey {
  const c = (raw || '').toLowerCase();
  if (c === 'call' || c === 'phone' || c === 'voice') return 'call';
  if (c === 'email') return 'email';
  return 'sms';
}

/** Minutes between now and an ISO timestamp (negative = future). */
function minutesSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 60000;
}

const REL = (m: number): string =>
  m < 1 ? 'now' : m < 60 ? `${Math.round(m)}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;

/** Relative label from an ISO timestamp. Future scheduledAt reads as
 *  "in 30m / in 2h / in 3d"; a timestamp that has already elapsed shows
 *  its scheduled date/time (in the org timezone) so an outdated nextTouch
 *  row doesn't quietly render as "in 0m". */
function formatUntil(iso: string | null | undefined, timezone: string): string {
  const past = minutesSince(iso);
  if (past === null) return '';
  if (past > 0) {
    // Scheduled time has already elapsed — read it as a relative day
    // ("Today" / "Yesterday" / "3 days ago") rather than clamping to zero.
    return getRelativeDateString(iso as string, { timezone });
  }
  const minutes = -past;
  if (minutes < 60) return `in ${Math.round(minutes)}m`;
  if (minutes < 24 * 60) return `in ${Math.round(minutes / 60)}h`;
  if (minutes < 7 * 24 * 60) return `in ${Math.round(minutes / (24 * 60))}d`;
  return `in ${Math.round(minutes / (7 * 24 * 60))}w`;
}

/* Mirror of CircularLeadScoreDisplay (md size). */
function ScoreBadge({ score }: { score: number }) {
  const capped = Math.min(score, 99);
  const color =
    capped >= 50 ? 'text-green-500 border-green-500' :
    capped >= 25 ? 'text-amber-500 border-amber-500' :
    capped > 0   ? 'text-red-500 border-red-500' :
                   'text-blue-500 border-blue-500';
  return (
    <div
      className={cn(
        'w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center border border-solid bg-card shadow-sm shrink-0',
        color
      )}
      title={`Lead score · ${capped}%`}
    >
      <span className={cn('font-bold tabular-nums text-[9px] sm:text-[10px] md:text-xs', color.split(' ')[0])}>
        {capped}
      </span>
    </div>
  );
}

/* Per-channel activity bars. Bar width caps so the count sits right
   beside it instead of being pushed to the column edge. */
function ChannelActivity({
  activity,
  flywheelEnabled,
}: {
  activity: GQLHotLead['touchCounts'];
  flywheelEnabled: boolean;
}) {
  const calls = activity.calls;
  const totalSms = activity.sms;
  const totalEmail = activity.emails;
  const max = Math.max(calls, totalSms, totalEmail, 1);

  const Row = ({ icon, value, color, label }: { icon: React.ReactNode; value: number; color: string; label: string }) => (
    <div className="flex items-center gap-2 min-w-0" title={`${value} ${label}`}>
      <span className="inline-flex items-center justify-center w-3.5 shrink-0" style={{ color }}>
        {icon}
      </span>
      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden min-w-0">
        <div
          className="h-full rounded-full"
          style={{ width: `${(value / max) * 100}%`, background: color }}
        />
      </div>
      <span className="text-[10.5px] tabular-nums font-semibold text-foreground/80 min-w-[18px] text-right shrink-0">
        {value}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-1 min-w-0">
      <Row icon={<Phone className="w-3 h-3" />} value={calls} color="#059669" label="calls" />
      {/* SMS is a flywheel-only channel — hidden for non-flywheel orgs. */}
      {flywheelEnabled && (
        <Row icon={<MessageSquare className="w-3 h-3" />} value={totalSms} color="#7c3aed" label="SMS" />
      )}
      <Row icon={<Mail className="w-3 h-3" />} value={totalEmail} color="#2563eb" label="emails" />
    </div>
  );
}

function lastFromShape(channel: ChannelKey, inbound: boolean): {
  type: string;
  sourceData?: Record<string, unknown>;
} {
  // Direction drives the call glyph + colour: inbound (lead → Remi) is blue
  // PhoneIncoming, outbound (Remi → lead) is green PhoneOutgoing.
  if (channel === 'call') {
    return { type: 'call', sourceData: { metadata: { direction: inbound ? 'inbound' : 'outbound' } } };
  }
  return { type: 'email' };
}

/** Action label for the last touch. `fromRemi` flips the actor: the
 *  inbound (lead) phrasing reads "replied via SMS / called Remi", the
 *  outbound (Remi) phrasing reads "Remi texted / Remi called". */
function actionLabel(channel: ChannelKey, text: string, fromRemi: boolean): string {
  const isVoicemail = text.toLowerCase().includes('voicemail');
  if (fromRemi) {
    if (channel === 'sms') return 'Remi texted';
    if (channel === 'call') return isVoicemail ? 'Remi left a voicemail' : 'Remi called';
    return 'Remi emailed';
  }
  if (channel === 'sms') return 'replied via SMS';
  if (channel === 'call') return isVoicemail ? 'left a voicemail' : 'called Remi';
  return 'replied via email';
}

/* Pixel-for-pixel mirror of a RecentActivities row: same icon avatar
   chrome, same `text-xs sm:text-sm` action title + uppercase timestamp,
   description rendered via the shared `ActivityDescriptionBlock` so
   long quotes get the same show-more behavior. Outcome badge + the
   "View call" / "Open thread" affordances reuse `ActivityInlineBadges`
   so they match Recent Activities exactly (and SMS rows show no outcome). */
interface LastTouchProps {
  lf: GQLHotLead['lastFromLead'];
  remi: GQLHotLead['lastFromRemi'];
  contactId: string;
  contactName: string | null;
  contactPhone: string | null;
  onViewCall: (callId: string) => void;
  onViewSms: (c: { contactId: string; contactName: string | null; contactPhone: string | null }) => void;
  flywheelEnabled: boolean;
}

const isSmsTouch = (t: { channel?: string | null } | null | undefined) =>
  !!t?.channel && toChannelKey(t.channel) === 'sms';

function LastFromLead({ lf, remi, contactId, contactName, contactPhone, onViewCall, onViewSms, flywheelEnabled }: LastTouchProps) {
  // The column shows the lead's last inbound touch; when there isn't one
  // yet, fall back to Remi's last outbound touch so the row still says
  // something. `fromRemi` flips the phrasing and the call direction.
  //
  // SMS is a flywheel-only channel, so for non-flywheel orgs an SMS touch is
  // skipped outright — neither its copy nor its "Open thread" drill-in should
  // surface. Skipping falls through to the other touch, or to "No activity
  // yet" when both are SMS.
  const visibleLf = flywheelEnabled || !isSmsTouch(lf) ? lf : null;
  const visibleRemi = flywheelEnabled || !isSmsTouch(remi) ? remi : null;
  const touch = visibleLf ?? visibleRemi;
  const fromRemi = !visibleLf && !!visibleRemi;
  if (!touch || !touch.channel) {
    return (
      <div className="flex items-center min-w-0">
        <div className="rounded-full p-1.5 md:p-2 mr-2.5 md:mr-3 bg-muted text-muted-foreground shrink-0">
          <Clock className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs sm:text-sm text-muted-foreground/70">No activity yet</span>
      </div>
    );
  }
  const channel = toChannelKey(touch.channel);
  const preview = touch.preview || '';
  const ago = minutesSince(touch.occurredAt);
  const isSms = channel === 'sms';
  const isCall = channel === 'call';
  const shape = lastFromShape(channel, !fromRemi);
  const icon = isSms
    ? <MessageSquare className="h-3.5 w-3.5 text-violet-700 dark:text-violet-300" />
    : getActivityIcon(shape as Parameters<typeof getActivityIcon>[0], 'h-3.5 w-3.5');
  const iconBg = isSms ? 'bg-violet-100 dark:bg-violet-500/15' : getActivityIconBg(shape);
  // Summary is the AI-condensed view of the touch — show it instead of the
  // raw preview when both exist (summary is usually clearer).
  const summary = touch.summary?.trim() || '';
  const detail = summary || preview;
  const hasDetail = Boolean(detail);
  return (
    <div className={cn('group flex min-w-0', hasDetail ? 'items-start' : 'items-center')}>
      <div
        className={cn(
          'rounded-full p-1.5 md:p-2 mr-2.5 md:mr-3 shrink-0',
          hasDetail && 'mt-0.5',
          iconBg
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
            <span className="text-xs sm:text-sm text-muted-foreground">
              {actionLabel(channel, `${preview} ${touch.outcome ?? ''}`, fromRemi)}
            </span>
            <ActivityInlineBadges
              eventType={channel}
              durationSeconds={isCall ? touch.durationSeconds ?? undefined : undefined}
              outcome={isCall ? touch.outcome ?? undefined : undefined}
              onViewCall={
                isCall && touch.callId ? () => onViewCall(touch.callId!) : undefined
              }
              onViewSms={
                isSms && contactId
                  ? () => onViewSms({ contactId, contactName, contactPhone })
                  : undefined
              }
            />
          </div>
          {ago !== null && (
            <span className="text-[10px] xl:text-xs text-muted-foreground uppercase whitespace-nowrap shrink-0">
              {REL(ago)} ago
            </span>
          )}
        </div>
        {hasDetail && (
          <ActivityDescriptionBlock description={summary ? detail : `“${detail}”`} maxLength={90} />
        )}
      </div>
    </div>
  );
}

interface LeadActivityProps {
  onContactClick?: (contactId: string) => void;
}

export const LeadActivity: FC<LeadActivityProps> = memo(({ onContactClick }) => {
  const organizationId = useOrganizationStore((state) => state.organization?.id);
  const timezone = useOrganizationStore((state) => state.organization?.timezone) || 'UTC';
  const apolloClient = useApolloClient();
  // Server-side hotLeads search: typed input is debounced and forwarded as
  // the `search` arg so the backend can match across every scored lead
  // (name + email + phone, with phone-format normalization).
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);
  // Default = "Hottest first". "Most recent" is the equivalent of the old
  // Recent Activities feed (freshest reply at the top) — available via the
  // sort menu when the agent wants that view.
  const [sortBy, setSortBy] = useState<'score' | 'lastFrom'>('score');

  // Quick-action modals opened from a row's last-touch cell — mirror of
  // RecentActivities. The whole-row click still opens the contact edit
  // modal; these icon affordances stopPropagation and open a focused viewer.
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  // Track that the user clicked a call row so we can open the modal in
  // a loading state immediately — without this, the modal only appears
  // once the fetch resolves, which read as a 200-400ms lag between
  // click and any visible feedback.
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null);
  const [selectedSmsContact, setSelectedSmsContact] = useState<{
    contactId: string;
    contactName: string | null;
    contactPhone: string | null;
  } | null>(null);

  // The hot-leads feed only carries a callId for the last touch, so fetch
  // the canonical call (transcript / timeline / recording) on demand. The
  // modal opens the moment the user clicks (loading skeleton inside);
  // `selectedCall` populates once the fetch lands.
  const handleViewCall = async (callId: string) => {
    setSelectedCallId(callId);
    setSelectedCall(null);
    const record = await fetchCallRecordById(apolloClient, organizationId, callId);
    if (record) setSelectedCall(record);
  };

  // Next touch reflects Remi's flywheel cadence — only show the column when
  // the flywheel is enabled for the org.
  const flywheelEnabled = useFlywheelEnabled();

  const { leads, loading, hasMore, isLoadingMore, loadMore } = useHotLeadsGQL({
    limit: 20,
    orderBy: 'score_desc',
    search: debouncedSearch || undefined,
    enabled: sortBy === 'score',
  });

  // Server already filtered by `search`; no extra client-side pass needed.
  const filtered = leads;

  // Infinite scroll: IntersectionObserver on a sentinel pinned to the
  // bottom of the feed body. Triggers `loadMore` well before the user
  // reaches the actual bottom (rootMargin 200px) so they never see an
  // empty gap. Same pattern as RecentActivities — `loadingMoreRef`
  // guards against multiple concurrent fires while a fetch is in
  // flight.
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return;

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (loadingMoreRef.current) return;
        loadingMoreRef.current = true;
        loadMore().finally(() => {
          loadingMoreRef.current = false;
        });
      },
      { root, rootMargin: '200px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [hasMore, loadMore]);

  /* Grid template:
     - Lead   — 200-280px (rigid so it sits tight against activity)
     - Remi   — 260-360px (kept close to Lead)
     - Spacer — visual breathing room before Last from lead
     - Last   — flex remainder

     `gap-3` between columns and a wider gap created via the pl-8 on the
     Last-from-lead cell so Lead+Remi sit close, then the right column
     gets visible separation. */
  /* Row + header grid:
     - Mobile: single column — Lead block on top, activity in the middle,
       last-from-lead at the bottom.
     - lg+: three columns with proper minmax sizing. */
  // Four columns on lg+: Lead, Next touch, Remi's activity, Last from lead.
  // Lead gets a comfortable fixed-ish width (name + phone room); Next
  // touch is compact; Remi's activity needs space for the bars; Last
  // from lead absorbs the remaining width (variable message preview).
  // Next-touch column only present when the flywheel is on — drop its
  // track from the grid otherwise so the remaining columns reflow.
  // Last-from-lead carries the variable message preview, so it gets the
  // widest track; Next/Last touch is the most compact. Tracks (lg+):
  //   with flywheel:    Lead 1 · Next/Last 0.9 · Remi 1.4 · Last 1.7
  //   without flywheel: Lead 1 · Remi 1.4 · Last 1.7
  const gridTemplate = flywheelEnabled
    ? 'grid-cols-1 lg:grid-cols-[1fr_0.9fr_1.4fr_1.7fr]'
    : 'grid-cols-1 lg:grid-cols-[1fr_1.4fr_1.7fr]';

  // "Most recent" reuses the existing all-logs RecentActivities feed —
  // same component, same data — and shares the same ghost sort dropdown
  // via its headerActions slot so the agent can switch back inline.
  if (sortBy === 'lastFrom') {
    return (
      <RecentActivities
        onContactClick={onContactClick}
        headerActions={
          <div className="feed-filter inline-flex">
            <Select
              variant="ghost"
              value={sortBy}
              onChange={(v) => setSortBy((Array.isArray(v) ? v[0] : v) as typeof sortBy)}
              options={SORT_OPTIONS}
            />
          </div>
        }
      />
    );
  }

  return (
    <div className="feed-card">
      <div className="feed-header">
        <div className="flex flex-col gap-2 lg:gap-0 lg:flex-row lg:items-center lg:justify-between w-full min-w-0">
          {/* LEFT cluster — title + search + stats. Stacks on mobile,
              inline on lg+. */}
          <div className="flex flex-col gap-2 lg:gap-0 lg:flex-row lg:items-center lg:flex-wrap lg:gap-x-4 lg:gap-y-2 min-w-0 lg:flex-1">
            <div className="flex items-center justify-between gap-3 min-w-0">
              <h3 className="whitespace-nowrap">Lead Activity</h3>
              {/* Mobile sort sits next to title. */}
              <div className="feed-filter inline-flex shrink-0 lg:hidden">
                <Select
                  variant="ghost"
                  value={sortBy}
                  onChange={(v) => setSortBy((Array.isArray(v) ? v[0] : v) as typeof sortBy)}
                  options={SORT_OPTIONS}
                />
              </div>
            </div>

            <div className="flex items-center gap-1.5 w-full lg:w-auto lg:min-w-[260px] lg:max-w-[320px] px-2 py-1 rounded-md border border-white/20 bg-white/[0.14] focus-within:border-white/40 focus-within:bg-white/[0.2] transition-colors">
              <Search className="w-3 h-3 text-white/70 shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, email, or phone…"
                className="flex-1 min-w-0 text-xs bg-transparent outline-none text-white placeholder:text-white/55"
              />
            </div>

            {filtered.length > 0 && (
              <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/70 tabular-nums whitespace-nowrap">
                <LiveIndicator size="sm" showLabel />
              </div>
            )}
          </div>

          {/* Sort — only on lg+ since mobile shows it next to the title. */}
          <div className="feed-filter hidden lg:inline-flex shrink-0">
            <Select
              variant="ghost"
              value={sortBy}
              onChange={(v) => setSortBy((Array.isArray(v) ? v[0] : v) as typeof sortBy)}
              options={SORT_OPTIONS}
            />
          </div>
        </div>
      </div>

      {/* Column header — matches speculo-ui Table thead. */}
      <div className={cn('hidden lg:grid', gridTemplate, 'items-center gap-6 xl:gap-8 px-4 py-2.5 border-b border-foreground/15 bg-table-header text-xs font-medium uppercase tracking-wider text-foreground/70')}>
        <div className="truncate">Lead</div>
        {flywheelEnabled && <div className="truncate">Next / Last touch</div>}
        <div className="truncate">Remi&apos;s activity</div>
        <div className="truncate">Last from lead</div>
      </div>

      {/* Body */}
      <div ref={scrollRef} className="feed-body scrollbar-custom !p-0">
        {loading &&
          filtered.length === 0 &&
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'grid animate-pulse',
                gridTemplate,
                'gap-3 lg:gap-6 xl:gap-8 px-3 sm:px-4 py-3 border-b border-foreground/10'
              )}
            >
              {/* Lead — score circle + name/phone */}
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="hidden lg:block w-8 h-8 rounded-full bg-muted shrink-0" />
                <div className="flex flex-col gap-1.5 min-w-0 flex-1">
                  <div className="h-3.5 w-28 rounded bg-muted" />
                  <div className="h-3 w-20 rounded bg-muted" />
                </div>
              </div>
              {/* Next touch */}
              {flywheelEnabled && (
                <div className="hidden lg:flex items-start gap-2">
                  <div className="w-5 h-5 rounded-md bg-muted shrink-0" />
                  <div className="flex flex-col gap-1.5 flex-1">
                    <div className="h-3 w-12 rounded bg-muted" />
                    <div className="h-3 w-24 rounded bg-muted" />
                  </div>
                </div>
              )}
              {/* Remi's activity — 3 bars */}
              <div className="hidden lg:flex flex-col gap-1.5">
                <div className="h-1.5 w-full rounded-full bg-muted" />
                <div className="h-1.5 w-4/5 rounded-full bg-muted" />
                <div className="h-1.5 w-3/5 rounded-full bg-muted" />
              </div>
              {/* Last from lead — icon + lines */}
              <div className="hidden lg:flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-muted shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <div className="h-3 w-24 rounded bg-muted" />
                  <div className="h-3 w-40 rounded bg-muted" />
                </div>
              </div>
            </div>
          ))}
        {!loading && filtered.length === 0 && (
          <div className="flex flex-1 items-center justify-center py-10 text-muted-foreground text-sm">
            No leads match
          </div>
        )}
        {filtered.map((lead) => {
          const isHot = lead.currentScore >= 80;
          return (
            <button
              key={lead.contactId}
              type="button"
              onClick={() => onContactClick?.(lead.contactId)}
              className={cn(
                'w-full text-left grid',
                gridTemplate,
                'gap-3 lg:gap-6 xl:gap-8 px-3 sm:px-4 py-3 border-b border-foreground/10 hover:bg-muted/30 transition-colors',
                isHot && 'bg-gradient-to-r from-orange-50 via-transparent to-transparent dark:from-orange-500/[0.06] dark:via-transparent dark:to-transparent'
              )}
            >
              {/* Lead column.
                  - Mobile: name on the left with the score pushed to the
                    right (single line), phone below.
                  - lg+: score sits on the LEFT of the name+phone stack
                    (the original desktop position). */}
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="hidden lg:inline shrink-0">
                  <ScoreBadge score={lead.currentScore} />
                </span>
                <div className="flex flex-col min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold text-foreground truncate">
                      {lead.name || 'Unknown'}
                    </span>
                    <span className="ml-auto shrink-0 lg:hidden">
                      <ScoreBadge score={lead.currentScore} />
                    </span>
                  </div>
                  {lead.phone && (
                    <span className="text-[11px] text-muted-foreground truncate tabular-nums mt-0.5">
                      {formatPhoneNumber(lead.phone)}
                    </span>
                  )}
                </div>
              </div>

              {/* Next touch — channel chip + when + reason. Flywheel-only.
                  Mobile gets a small eyebrow label since column headers are
                  hidden below lg. */}
              {flywheelEnabled && (
                <div className="min-w-0 pt-2 lg:pt-0 border-t border-foreground/5 lg:border-0">
                  <div className="lg:hidden text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1">
                    Next / Last touch
                  </div>
                  {lead.nextTouch ? (
                    (() => {
                      const ch = NEXT_TOUCH_CHANNEL_META[toChannelKey(lead.nextTouch.channel)];
                      const until = formatUntil(lead.nextTouch.scheduledAt, timezone);
                      return (
                        <div className="flex items-start gap-2 min-w-0">
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-md flex-shrink-0 mt-0.5"
                            style={{ color: ch.color, backgroundColor: `${ch.color}1a` }}
                          >
                            {ch.icon}
                          </span>
                          <div className="min-w-0 flex flex-col">
                            {until && (
                              <span className="text-xs sm:text-sm text-foreground tabular-nums">
                                {until}
                              </span>
                            )}
                            {lead.nextTouch.reason && (
                              <span className="text-xs sm:text-sm text-muted-foreground truncate">
                                {lead.nextTouch.reason}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <span className="text-xs sm:text-sm text-muted-foreground italic">—</span>
                  )}
                </div>
              )}

              {/* Remi's activity — under the lead on mobile, beside it on lg+.
                  Mobile gets the same eyebrow as Next touch / Last from lead. */}
              <div className="min-w-0 pt-2 lg:pt-0 border-t border-foreground/5 lg:border-0">
                <div className="lg:hidden text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1">
                  Remi&apos;s activity
                </div>
                <ChannelActivity activity={lead.touchCounts} flywheelEnabled={flywheelEnabled} />
              </div>

              {/* Last from lead — wraps below on mobile, pl-8 on lg+ to
                  set it off from the activity column. Mobile gets the
                  same eyebrow as Next touch. */}
              <div className="min-w-0 pt-2 lg:pt-0 border-t border-foreground/5 lg:border-0">
                <div className="lg:hidden text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground mb-1">
                  Last from lead
                </div>
                <LastFromLead
                  lf={lead.lastFromLead}
                  remi={lead.lastFromRemi}
                  contactId={lead.contactId}
                  contactName={lead.name}
                  contactPhone={lead.phone}
                  onViewCall={handleViewCall}
                  onViewSms={setSelectedSmsContact}
                  flywheelEnabled={flywheelEnabled}
                />
              </div>
            </button>
          );
        })}
        {isLoadingMore && (
          <div className="flex justify-center py-3">
            <div className="h-4 w-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
          </div>
        )}
        {/* IntersectionObserver target — sits at the bottom of the
            scrollable feed body so loadMore() fires well before the
            user reaches the visible end. */}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-1 w-full" />}
      </div>
      <CallDetailsModal
        isOpen={!!selectedCallId}
        onClose={() => {
          setSelectedCallId(null);
          setSelectedCall(null);
        }}
        call={selectedCall}
        isLoading={!!selectedCallId && !selectedCall}
      />
      <SmsThreadModal
        isOpen={!!selectedSmsContact}
        onClose={() => setSelectedSmsContact(null)}
        contactId={selectedSmsContact?.contactId ?? null}
        contactName={selectedSmsContact?.contactName ?? null}
        contactPhone={selectedSmsContact?.contactPhone ?? null}
        onContactClick={onContactClick}
      />
    </div>
  );
});

LeadActivity.displayName = 'LeadActivity';
